/**
 * Working out which period a worksheet holds.
 *
 * A year of trial balances often arrives as one workbook with a sheet per
 * month. The sheet names come in two families:
 *
 *   accounting periods — "P1", "P 2", "Period 3", or a bare "4"
 *   calendar months    — "Jan", "January", "Jan-24", "2024-01", "Mar 2024"
 *
 * A period number means nothing without the fiscal year: P1 of a June
 * year-end business is July. So the file's year and the fiscal year end are
 * both needed, and the year comes from the filename.
 *
 * Anything unrecognised returns null and the analyst picks the period by
 * hand, which is the same contract as filename parsing.
 */

export interface Period {
  year: number;
  month: number;
}

const MONTH_NAMES: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11,
  dec: 12, december: 12,
};

const MONTH_ALT =
  "january|february|march|april|june|july|august|september|october|november|december|sept|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec";

/** Sheets that are never a trial balance. */
const NON_TB_RE =
  /^(cover|contents|index|toc|table of contents|summary|summaries|notes?|instructions?|readme|glossary|key|legend|assumptions|control|checks?|pivot|chart|graph|data ?validation|lookups?|mapping|map|ref(erence)?s?)\b/i;

export const looksLikeNonTbSheet = (name: string): boolean => NON_TB_RE.test(String(name ?? "").trim());

/** First calendar month of a fiscal year. */
export function fiscalYearStart(fy: number, fyEndMonth: number): Period {
  if (fyEndMonth === 12) return { year: fy, month: 1 };
  return { year: fy - 1, month: fyEndMonth + 1 };
}

/** Which fiscal year a calendar month falls in. */
export const fiscalYearOf = (p: Period, fyEndMonth: number): number =>
  p.month <= fyEndMonth ? p.year : p.year + 1;

/** Advance a month by n steps. */
function addMonths(p: Period, n: number): Period {
  const total = p.year * 12 + (p.month - 1) + n;
  return { year: Math.floor(total / 12), month: (total % 12) + 1 };
}

export type PeriodSource = "period_number" | "month_name" | "iso" | "none";

export interface SheetPeriod {
  period: Period;
  source: PeriodSource;
}

/**
 * Read a sheet name as a period.
 *
 * `fiscalYearHint` is the fiscal year the workbook covers, normally taken
 * from its filename. A period number cannot be resolved without it; a fully
 * dated sheet name does not need it.
 */
export function parseSheetPeriod(
  sheetName: string,
  fiscalYearHint: number | null,
  fyEndMonth: number,
): SheetPeriod | null {
  const s = String(sheetName ?? "").trim();
  if (!s) return null;

  // A fiscal year written on the sheet itself beats the filename's, and its
  // digits are then removed so "FY25" cannot be misread as a month or a
  // period number further down.
  let fy = fiscalYearHint;
  const fyOnSheet = s.match(/\bFY\s?(\d{2}|\d{4})\b/i);
  if (fyOnSheet) {
    const raw = fyOnSheet[1];
    const y = raw.length === 2 ? 2000 + parseInt(raw, 10) : parseInt(raw, 10);
    if (y >= 1990 && y <= 2100) fy = y;
  }
  const rest = s.replace(/\bFY\s?\d{2,4}\b/gi, " ");

  const fromPeriodNumber = (n: number): SheetPeriod | null => {
    if (n < 1 || n > 12 || fy == null) return null;
    return { period: addMonths(fiscalYearStart(fy, fyEndMonth), n - 1), source: "period_number" };
  };

  // ── full dates first, so "2024-01" never reads as period 2024 ──
  let m = rest.match(/(?:^|[^\d])(\d{4})[-._/](0?[1-9]|1[0-2])(?:[-._/]\d{1,2})?(?:[^\d]|$)/);
  if (m) {
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    if (year >= 1990 && year <= 2100) return { period: { year, month }, source: "iso" };
  }

  // ── month name, with or without a year ──
  m = rest.match(new RegExp("(?<![a-z])(" + MONTH_ALT + ")(?![a-z])[\\s._'-]*(\\d{4}|\\d{2})?", "i"));
  if (m) {
    const month = MONTH_NAMES[m[1].toLowerCase()];
    if (month) {
      if (m[2]) {
        const raw = m[2];
        const year = raw.length === 2 ? 2000 + parseInt(raw, 10) : parseInt(raw, 10);
        if (year >= 1990 && year <= 2100) return { period: { year, month }, source: "month_name" };
      }
      if (fy != null) {
        // No year on the sheet: place the month inside the workbook's fiscal year.
        const year = month <= fyEndMonth ? fy : fy - 1;
        return { period: { year, month }, source: "month_name" };
      }
      return null;
    }
  }

  // ── accounting period number, anywhere in the name ──
  // "P1", "Period 3", "Per 3", and the same inside "TB FY25 P1".
  m = rest.match(/(?:^|[^a-z0-9])(?:period|per|p)\s*[-_.]?\s*(\d{1,2})(?!\d)/i);
  if (m) {
    const hit = fromPeriodNumber(parseInt(m[1], 10));
    if (hit) return hit;
  }

  // ── a bare number, which numbers the period the same way ──
  // "01", and the trailing number of "TB FY25 01".
  const numbers = rest.match(/(?:^|[^a-z0-9])(\d{1,2})(?!\d)/gi);
  if (numbers && numbers.length > 0) {
    const last = numbers[numbers.length - 1].match(/(\d{1,2})/);
    if (last) {
      const hit = fromPeriodNumber(parseInt(last[1], 10));
      if (hit) return hit;
    }
  }

  return null;
}

/**
 * Period for every sheet in a workbook. Sheets whose names say they are not
 * trial balances come back excluded, and so does anything the caller could
 * not place — those are the ones needing a person.
 */
export interface SheetPlan {
  sheetName: string;
  include: boolean;
  period: Period | null;
  source: PeriodSource;
}

/** What one uploaded file holds, as the analyst declared before uploading. */
export type FileLayout = "month" | "fy" | "multi";

/**
 * Which sheets of a workbook to use, and what period each one states.
 *
 * The rule that matters is the last one: a workbook declared to hold several
 * months must never come back with nothing selected. When no sheet name
 * gives a period away — usually because the filename carries no year to
 * anchor "P1" against — every plausible sheet is included undated, so the
 * analyst sees rows to fill in rather than an empty screen.
 */
export function planWorkbook(
  sheetNames: string[],
  layout: FileLayout,
  yearHint: number | null,
  fyEndMonth: number,
  filePeriod: Period | null,
): SheetPlan[] {
  const plan = planSheets(sheetNames, yearHint, fyEndMonth, filePeriod);

  if (layout === "multi") {
    const anyDated = plan.some((x) => x.include && x.period);
    if (anyDated) return plan.map((x) => ({ ...x, include: x.include && !!x.period }));
    return plan.map((x) => ({ ...x, period: null }));
  }

  // One period for the whole workbook: the first sheet that could be a trial
  // balance carries it, and the rest stay available in the sheet chooser.
  const first = plan.find((x) => x.include) ?? plan[0];
  const period = layout === "fy" && (filePeriod || yearHint) != null
    ? { year: filePeriod?.year ?? (yearHint as number), month: fyEndMonth }
    : filePeriod;
  return plan.map((x) => ({
    ...x,
    include: x.sheetName === first?.sheetName,
    period: x.sheetName === first?.sheetName ? period : null,
  }));
}

export function planSheets(
  sheetNames: string[],
  fiscalYearHint: number | null,
  fyEndMonth: number,
  fallback: Period | null,
): SheetPlan[] {
  const parsed = sheetNames.map((name) => ({ name, hit: parseSheetPeriod(name, fiscalYearHint, fyEndMonth) }));
  const dated = parsed.filter((p) => p.hit && !looksLikeNonTbSheet(p.name));

  return parsed.map(({ name, hit }) => {
    if (looksLikeNonTbSheet(name)) {
      return { sheetName: name, include: false, period: null, source: "none" as const };
    }
    if (hit) return { sheetName: name, include: true, period: hit.period, source: hit.source };
    // A workbook whose other sheets are clearly periods: this one is the odd
    // one out, so leave it out rather than guessing a period for it.
    if (dated.length > 0) return { sheetName: name, include: false, period: null, source: "none" as const };
    // Single-sheet workbooks fall back to whatever the filename gave us.
    return { sheetName: name, include: true, period: fallback, source: "none" as const };
  });
}
